import { AnyAction } from 'redux';
import {
  FETCH_FILE_TYPES_SUCCESS,
  FETCH_STRUCTURE_SUCCESS,
  FETCH_STRUCTURE_FAILURE,
  UPDATE_FOLDERS_WITH_DOCUMENTS,
  FILE_IMPORT_CONNECTING,
  FILE_IMPORT_CONNECTED,
  FILE_IMPORT_STEP,
  FILE_IMPORT_STEPS,
  FILE_IMPORT_COMPLETED,
  FILE_IMPORT_FAILED,
  FILE_IMPORT_RESET,
  FILE_IMPORT_DONE,
} from '../types';
import { FolderProps } from '../../components/Files/documents/types';

const FROM_RAW: Record<string, "Pending" | "In Progress" | "Completed" | "Failed" | "Skipped"> = {
  pending: "Pending",
  in_progress: "In Progress",
  completed: "Completed",
  failed: "Failed",
  skipped: "Skipped",
};

type StepUI = {
  id: string;
  label: string;
  status: "Pending" | "In Progress" | "Completed" | "Failed" | "Skipped";
  messages: string[];
};
interface FoldersState {
  loading: boolean;
  folders: FolderProps[];
  fileTypes: any[];
  subFolders: any[];
  structure: any[];
  flatFiles: any[];
  error: string;
  isConnecting: boolean;
  connected: boolean;
  streamClosed: boolean;
  steps: StepUI[];
  lastStep?: StepUI | null;
  complete: boolean;
  ok: boolean | null;
  summary: any,
  uploadedCount: number;
  rejectedCount: number;
}

const initialState: FoldersState = {
  loading: false,
  folders: [],
  fileTypes: [],
  subFolders: [],
  structure: [],
  flatFiles: [],
  error: '',
  isConnecting: false,
  connected: false,
  steps: [],
  complete: false,
  ok: null,
  summary: null,
  lastStep: null,
  uploadedCount: 0,
  rejectedCount: 0,
  streamClosed: false,
};

function upsertStep(steps: StepUI[], incoming: Partial<StepUI> & { id: string }): StepUI[] {
  const idx = steps.findIndex((s) => s.id === incoming.id);

  if (idx === -1) {
    return [
      ...steps,
      {
        ...incoming, 
        id: incoming.id,
        label: incoming.label ?? incoming.id,
        status: (incoming.status as StepUI["status"]) || "Pending",
        messages: incoming.messages ?? [],
      },
    ];
  }

  const prev = steps[idx];
  const next: StepUI = {
    ...prev,
    ...incoming, 
    id: incoming.id,
    label: incoming.label ?? prev.label ?? incoming.id,
    status: (incoming.status as StepUI["status"]) || prev.status,
    messages: incoming.messages ?? prev.messages ?? [],
  };

  const copy = [...steps];
  copy[idx] = next;
  return copy;
}

const folderReducer = (state = initialState, action: AnyAction) => {
  switch (action.type) {
    case FETCH_FILE_TYPES_SUCCESS:
      return {
        ...state,
        fileTypes: action.payload,
      };
    case FETCH_STRUCTURE_SUCCESS:
      return {
        ...state,
        structure: action.payload.structure,
        flatFiles: action.payload.flatFiles,
        error: '',
        loading: false
      };
    case FETCH_STRUCTURE_FAILURE:
      return {
        ...state,
        error: action.payload,
      };
    case UPDATE_FOLDERS_WITH_DOCUMENTS:
      return {
        ...state,
        folders: action.payload,
      };
    case FILE_IMPORT_RESET:
      return { ...state, isConnecting: false, connected: false, steps: [], lastStep: null, ok: null, complete: false, summary: null, error: '', streamClosed: false };
    case FILE_IMPORT_CONNECTING:
      return { ...state, isConnecting: true, connected: false, complete: false, streamClosed: false, error: null };

    case FILE_IMPORT_CONNECTED:
      return { ...state, isConnecting: false, connected: true };
    case FILE_IMPORT_STEPS: {
      const steps = (action.payload?.steps || []).map((s: any) => ({
        id: String(s.step ?? s.id),
        label: s.name ?? s.label ?? `File ${s.step}`,
        status: FROM_RAW[s.status] || "Pending",
        messages: [],
      })) as StepUI[];
      return { ...state, steps };
    }

    case FILE_IMPORT_STEP: {
      const raw = action.payload || {};
      const id = String(raw.step ?? raw.id);
      const status = raw.status;
      const message = raw.message;
      const { step: _s, status: _st, message: _m, ...rest } = raw;
      const ui = FROM_RAW[status] || "Pending";

      const nextSteps = upsertStep(state.steps, {
        id,
        status: ui,
        messages: message
          ? (() => {
            const existing = state.steps.find((s) => s.id === id)?.messages || [];
            return [...existing, String(message)];
          })()
          : state.steps.find((s) => s.id === id)?.messages || [],
        ...rest,
      });

      const lastStep = nextSteps.find((s) => s.id === id) || null;
      return { ...state, steps: nextSteps, lastStep };
    }

    case FILE_IMPORT_COMPLETED: {
      const sum = action.payload || {};
      return {
        ...state,
        complete: true,
        ok: !!sum.ok,
        summary: sum,
        uploadedCount: typeof sum.uploadedCount === "number" ? sum.uploadedCount : state.uploadedCount,
        rejectedCount: typeof sum.rejectedCount === "number" ? sum.rejectedCount : state.rejectedCount,
        error: sum.ok ? null : (sum.message || "No files were accepted."),
      };
    }

    case FILE_IMPORT_FAILED: {
      const msg = action.payload?.error || action.payload?.message || "File import failed.";
      return {
        ...state,
        complete: true,
        ok: false,
        error: msg,
        summary: msg,
      };
    }

    case FILE_IMPORT_DONE:
      return { ...state, streamClosed: true };

    default:
      return state;
  }
};

export default folderReducer;
